import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SyncIcon from "@mui/icons-material/Sync";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, Link, Stack, Theme, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ManifestComplianceEscalation } from "generated-graphql/graphql";
import { useCallback } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { findManifestInRcraInfo } from "util/encampy/openManifestInRcraInfo";
import { runMultiPhaseAutomation } from "util/encampy/runMultiPhaseAutomation";

import {
  type ComplianceItem,
  type ComplianceItemEpa,
  type ComplianceItemMissing,
  type DeadlineBand,
  getDeadlineBand,
  getDeadlineDisplay,
} from "./CompliancePane.helpers";
import { getEManifestComplianceDisplay } from "./eManifestComplianceDisplay";
import { RowActionButton } from "./RowActionButton";

// Figma --primary/main differs from theme.primary.main; remove once theme has a token for it.
const FIGMA_PRIMARY_BLUE = "#2196F3";

function rcraInfoSiteDraftsUrl(epaId: string): string {
  return `https://rcrainfo.epa.gov/rcrainfoprod/action/industry/secured/e-manifest/drafts/${epaId}#!/`;
}

type EmanifestNavigationResult = "navigated" | "fallback-opened" | "fallback-blocked";

async function openManifestWithEncampyOrFallback(
  epaId: string,
  mtn: string | null
): Promise<EmanifestNavigationResult> {
  const draftsUrl = rcraInfoSiteDraftsUrl(epaId);

  // Without an MTN the chain would type an empty filter and click the first
  // View link, opening an unrelated manifest.
  if (!mtn) {
    const opened = window.open(draftsUrl, "_blank", "noopener,noreferrer");
    return opened ? "fallback-opened" : "fallback-blocked";
  }

  try {
    await runMultiPhaseAutomation(draftsUrl, async (runner) => {
      await runner.run((tab) => findManifestInRcraInfo(tab, mtn));
    });
    return "navigated";
  } catch (err) {
    console.warn("Encampy RCRAInfo manifest navigation failed; opening fallback tab:", err);
    // User-gesture token was consumed by the preceding await, so popup blockers may return null.
    const opened = window.open(draftsUrl, "_blank", "noopener,noreferrer");
    return opened ? "fallback-opened" : "fallback-blocked";
  }
}

export interface ComplianceCardProps {
  item: ComplianceItem;
  now: Date;
  activePreviewDocId?: string | null;
  onViewDocument: (documentId: string, fileExtension: string | null) => void;
  onResolveInEManifest: (discrepancyId: string) => void;
  onAddProofOfContact: (processedManifestId: string) => void;
  onCreateExceptionReport: (processedManifestId: string) => void;
  creatingExceptionReport?: boolean;
  /**
   * The manifest's tenant, used to build the proof-of-contact document and
   * exception-report submission links. The staff review queue is non-tenanted,
   * so `useParams()` has no tenantId there — without this the links (and thus an
   * existing exception report) never render.
   */
  tenantId?: string;
}

function bandColor(theme: Theme, band: DeadlineBand): string {
  switch (band) {
    case "crit":
      return theme.palette.error.dark;
    case "warn":
      return theme.palette.warning.main;
    case "due":
      return FIGMA_PRIMARY_BLUE;
  }
}

function DeadlineDot({ color }: { color: string }) {
  return (
    <Box
      sx={{
        width: "0.75rem",
        height: "0.75rem",
        borderRadius: "50%",
        flexShrink: 0,
        // Radial highlight upper-left fades to transparent, base color underneath.
        background: `radial-gradient(circle at 32% 30%, ${alpha("#fff", 0.7)} 0%, ${alpha("#fff", 0.25)} 22%, ${alpha("#fff", 0)} 50%), ${color}`,
        boxShadow: `inset 0 -1px 1px ${alpha("#000", 0.18)}`,
      }}
    />
  );
}

/**
 * The manifest an expanded compliance row is about. Its own export because the
 * deadline pill that used to sit beside it now renders in the row's Status
 * column, so the two are no longer one header.
 */
export function HeaderRow({ item }: { item: ComplianceItem }) {
  return <ManifestLine item={item} />;
}

/**
 * How long is left on a compliance deadline, dot and all.
 *
 * Rendered by the pane into the expanded row's own Status cell rather than
 * pushed to the right edge of a cell spanning every column — that put it past
 * the Status column it belongs under, at the table's right edge.
 *
 * Renders nothing for a resolved item, which has no live deadline; the Status
 * column's chip is what says it is resolved.
 */
export function DeadlinePill({ item, now }: { item: ComplianceItem; now: Date }) {
  const theme = useTheme();
  if (item.status === "resolved") return null;
  const color = bandColor(theme, getDeadlineBand(item, now));
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <DeadlineDot color={color} />
      <Typography variant="body2" sx={{ color }}>
        {getDeadlineDisplay(item, now)}
      </Typography>
    </Stack>
  );
}

function ManifestLine({ item }: { item: ComplianceItem }) {
  const tracking = item.manifestTrackingNumber ?? item.processedManifestId.slice(0, 8);
  const route =
    item.generatorName && item.tsdfName
      ? `${item.generatorName} → ${item.tsdfName}`
      : (item.generatorName ?? item.tsdfName ?? "");
  // Only the tracking number carries weight; the facility route reads as
  // supporting detail. The regulator's request text is not repeated here: the
  // expander below pairs each comment with its own section marker.
  return (
    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
      <Box component="span" sx={{ fontWeight: 600 }}>
        {tracking}
      </Box>
      {route ? ` • ${route}` : null}
    </Typography>
  );
}

export function MissingBody({
  item,
  now,
  onAddProofOfContact,
  onCreateExceptionReport,
  creatingExceptionReport,
  tenantId: tenantIdProp,
}: {
  item: ComplianceItemMissing;
  now: Date;
  onAddProofOfContact: (processedManifestId: string) => void;
  onCreateExceptionReport: (processedManifestId: string) => void;
  creatingExceptionReport?: boolean;
  tenantId?: string;
}) {
  const theme = useTheme();
  // Prefer the manifest's tenant (passed down); fall back to the route param on
  // tenant-scoped customer routes. On the non-tenanted staff review queue
  // useParams() has no tenantId, so the prop is what makes the doc/submission
  // links render there.
  const { tenantId: routeTenantId } = useParams<{ tenantId: string }>();
  const tenantId = tenantIdProp ?? routeTenantId;
  const proofDocId = item.transporterContactProofDocumentId;
  const proofDate = item.transporterContactedAt?.slice(0, 10);
  const proofLabel = proofDate ?? "View document";
  const hasContact = item.transporterContactedAt != null;
  const submissionId = item.exceptionReportSubmissionId;
  const exceptionEligible = item.escalation === ManifestComplianceEscalation.Overdue_60;
  // Fulfillment actions live on the row they fulfill; hide them once resolved.
  const showActions = item.status !== "resolved";
  // The regulatory requirement for the current escalation level (e.g. "45+ days
  // since shipment — contact the transporter/TSDF"), flipping to the action-taken
  // variant once the user has responded. Same source of truth as the manifest list.
  // Hidden on resolved rows (mirroring the deadline pill): escalation persists
  // after auto-resolve, so the requirement copy would falsely demand action.
  const requirement =
    item.status !== "resolved"
      ? getEManifestComplianceDisplay({
          eManifestComplianceEscalation: item.escalation,
          exceptionReportSubmission: item.exceptionReportSubmissionId
            ? { status: item.exceptionReportSubmissionStatus }
            : null,
          transporterContactedAt: item.transporterContactedAt,
        })
      : null;
  return (
    <Stack spacing={0.75}>
      {item.shippedDate && (
        <Typography variant="caption" color="text.secondary">
          Shipped: {item.shippedDate}
        </Typography>
      )}
      {requirement && (
        // The card's call to action — same color as the deadline pill so the
        // urgency and the required step read as one message.
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: bandColor(theme, getDeadlineBand(item, now)) }}
        >
          {requirement.tooltipText}
        </Typography>
      )}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 30 }}>
        <Typography variant="body2">
          Proof of contact:{" "}
          {proofDocId && tenantId ? (
            <Link component={RouterLink} to={`/o/${tenantId}/documents/${proofDocId}`}>
              {proofLabel}
            </Link>
          ) : hasContact ? (
            (proofDate ?? "recorded")
          ) : (
            "–"
          )}
        </Typography>
        {showActions && (
          <Button
            size="small"
            variant={hasContact ? "text" : "outlined"}
            onClick={() => onAddProofOfContact(item.processedManifestId)}
          >
            {hasContact ? "Re-contact transporter" : "Add proof of contact"}
          </Button>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 30 }}>
        <Typography variant="body2">
          Exception report:{" "}
          {submissionId && tenantId ? (
            <Link component={RouterLink} to={`/o/${tenantId}/waste/submissions/${submissionId}`}>
              {item.exceptionReportTitle ?? "submission"}
            </Link>
          ) : (
            "–"
          )}
        </Typography>
        {showActions && !submissionId && (
          <Tooltip
            placement="top"
            title={
              exceptionEligible
                ? ""
                : "Available once the shipment passes the 60-day exception-report threshold."
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={!exceptionEligible || !!creatingExceptionReport}
                onClick={() => onCreateExceptionReport(item.processedManifestId)}
              >
                Create exception report
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
}

/** Quiet utility button: opens the manifest in RCRAInfo. Lives in the actions row. */
function EmanifestLinkButton({ item }: { item: ComplianceItem }) {
  const alerts = useAlerts();
  const handleClick = useCallback(async () => {
    if (!item.epaId) return;
    const result = await openManifestWithEncampyOrFallback(item.epaId, item.manifestTrackingNumber);
    if (result === "fallback-blocked") {
      alerts.error(
        "Your browser blocked the RCRAInfo tab. Allow pop-ups for this site and try again."
      );
    }
  }, [item.epaId, item.manifestTrackingNumber, alerts]);

  if (!item.epaId) return null;
  return (
    <Button
      size="small"
      variant="text"
      startIcon={<OpenInNewIcon />}
      onClick={handleClick}
      sx={{ color: "text.secondary" }}
    >
      Open in e-Manifest
    </Button>
  );
}

export function EpaBody({ item }: { item: ComplianceItemEpa }) {
  const theme = useTheme();
  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        Requested: {item.requestedAt} by {item.initiator}
        <br />
        Deadline: {item.deadline}
      </Typography>
      <Box sx={{ borderLeft: `1px solid ${theme.palette.grey[400]}`, pl: 1 }}>
        {item.sections.map((s, i) => (
          <Typography
            key={`${i}-${s.sectionNumber}`}
            variant="caption"
            sx={{ color: "text.primary", display: "block" }}
          >
            <strong>§ {s.sectionNumber} </strong>
            <span>– "{s.comments}"</span>
          </Typography>
        ))}
      </Box>
    </Stack>
  );
}

export function ActionsRow({
  item,
  isPreviewedDoc,
  onViewDocument,
  onResolveInEManifest,
}: Pick<ComplianceCardProps, "item" | "onViewDocument" | "onResolveInEManifest"> & {
  isPreviewedDoc: boolean;
}) {
  const theme = useTheme();
  const encampBlue = theme.palette.encampBlue.main;
  const viewDoc = (
    <RowActionButton
      key="view"
      variant="text"
      startIcon={<VisibilityIcon />}
      blocked={!item.sourceDocumentId}
      reason={item.sourceDocumentId ? "" : "No document attached to this manifest"}
      onClick={() => {
        if (item.sourceDocumentId) {
          onViewDocument(item.sourceDocumentId, item.sourceDocumentFileExtension);
        }
      }}
      sx={{
        bgcolor: isPreviewedDoc ? alpha(FIGMA_PRIMARY_BLUE, 0.04) : "transparent",
        color: FIGMA_PRIMARY_BLUE,
        "&:hover": {
          bgcolor: isPreviewedDoc
            ? alpha(FIGMA_PRIMARY_BLUE, 0.08)
            : alpha(FIGMA_PRIMARY_BLUE, 0.04),
        },
      }}
    >
      {isPreviewedDoc ? "Viewing document" : "View document"}
    </RowActionButton>
  );

  // Nothing trails a row that is no longer unresolved: the status chip in the
  // row's own Status column, directly above this, already says what it is.
  // Missing e-Manifest rows fulfill their actions inline on the proof and
  // exception rows (see MissingBody), so they never trail anything either.
  let trailing: React.ReactNode = null;
  if (item.status === "unresolved" && item.kind === "epaCorrection") {
    trailing = (
      <Tooltip
        placement="top"
        title={
          <Typography variant="caption" sx={{ display: "block", maxWidth: "17.5rem" }}>
            Marks this correction as being handled in RCRAInfo (e.g. you're updating the manifest
            values directly in EPA's portal). The row stays in <em>Pending</em> until the corrected
            data syncs back through the pipeline and either resolves automatically or reopens with
            the new values.
          </Typography>
        }
      >
        <Button
          size="small"
          variant="contained"
          startIcon={<SyncIcon />}
          onClick={() => onResolveInEManifest(item.id)}
          sx={{
            bgcolor: encampBlue,
            color: "common.white",
            boxShadow: 2,
            "&:hover": { bgcolor: alpha(encampBlue, 0.92) },
          }}
        >
          Resolve in e-Manifest
        </Button>
      </Tooltip>
    );
  }

  // One home for every row action: quiet utility links on the left, the
  // primary next step on the right.
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={1}
      sx={{ flexWrap: "wrap" }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {viewDoc}
        <EmanifestLinkButton item={item} />
      </Stack>
      {trailing}
    </Stack>
  );
}
